import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import Navbar from "@/components/Navbar";
import SiteFooter from "@/components/public/SiteFooter";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { CheckCircle2, XCircle, Loader2, Clock } from "lucide-react";

const MAX_ATTEMPTS = 8;

export default function PaymentStatus() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState("checking"); // checking | paid | failed | timeout
  const attempts = useRef(0);

  useEffect(() => {
    if (!sessionId) {
      setState("failed");
      return;
    }
    let active = true;
    const poll = async () => {
      if (attempts.current >= MAX_ATTEMPTS) {
        if (active) setState("timeout");
        return;
      }
      attempts.current += 1;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (!active) return;
        if (data.payment_status === "paid") return setState("paid");
        if (data.status === "expired") return setState("failed");
        setTimeout(poll, 2000);
      } catch {
        if (active) setTimeout(poll, 2000);
      }
    };
    poll();
    return () => {
      active = false;
    };
  }, [sessionId]);

  const views = {
    checking: { icon: <Loader2 className="h-12 w-12 animate-spin text-brand" />, title: "Confirming your payment…", sub: "Please wait a moment, this won't take long." },
    paid: { icon: <CheckCircle2 className="h-12 w-12 text-green-600" />, title: "Payment successful!", sub: "Your booking is confirmed. A driver will be assigned for pickup." },
    failed: { icon: <XCircle className="h-12 w-12 text-destructive" />, title: "Payment not completed", sub: "Your order is saved as unpaid. You can retry from your orders." },
    timeout: { icon: <Clock className="h-12 w-12 text-amber-500" />, title: "Still processing", sub: "We're confirming with Stripe. Check your orders shortly." },
  };
  const v = views[state];

  return (
    <div className="public-site flex min-h-screen flex-col bg-slate-50">
      <Navbar />
      <div className="mx-auto flex w-full max-w-lg flex-1 flex-col items-center px-5 py-24 text-center" data-testid="payment-status">
        {v.icon}
        <h1 className="mt-6 font-heading text-3xl font-bold tracking-tight text-slate-900" data-testid="payment-status-title">{v.title}</h1>
        <p className="mt-2 text-slate-500">{v.sub}</p>
        <div className="mt-8 flex gap-3">
          <Link to="/orders"><Button className="h-12 rounded-full bg-brand px-7 font-bold hover:bg-brand-hover" data-testid="view-orders-btn">View my orders</Button></Link>
          <Link to="/search"><Button variant="secondary" className="h-12 rounded-full px-7 font-bold" data-testid="book-again-btn">Book again</Button></Link>
        </div>
      </div>
      <SiteFooter />
    </div>
  );
}
